/**
 * Auth API wrappers for canonical authentication and account data operations.
 * Authorization and account-settings composition are resolved separately by
 * the backend settings-experience contract; these wrappers return data only.
 */
import {
  authLogin as authControllerLoginGenerated,
  authSelectContext as authControllerSelectContextGenerated,
  authChallengeTotp as authControllerVerifyChallengeTotpGenerated,
  authChallengeRecovery as authControllerVerifyChallengeRecoveryCodeGenerated,
  authPasswordForgot as authControllerForgotPasswordGenerated,
  authPasswordReset as authControllerResetPasswordGenerated,
  authLogout as authControllerLogoutGenerated,
  authRefresh as authControllerRefreshGenerated,
  authSwitchContext as authControllerSwitchContextGenerated,
  authDelegationEnter as authControllerEnterDelegationGenerated,
  authDelegationExit as authControllerExitDelegationGenerated,
  authPasswordChange as authControllerChangeInitialPasswordGenerated,
  authReadProfile as authControllerMeGenerated,
  settingsProfileUpdate as settingsProfileUpdateGenerated,
  settingsPreferences as settingsPreferencesReadGenerated,
  settingsPreferencesUpdate as settingsPreferencesUpdateGenerated,
} from '../../api/generated/client';
import type {
  LoginDto,
  LoginResponseDto,
  AvailableContextResponseDto,
  AuthenticatedUserResponseDto,
  ChallengeTotpDto,
  ChallengeRecoveryCodeDto,
  SelectContextDto,
  ForgotPasswordDto,
  ResetPasswordDto,
  ProfileResponse,
  AccessTokenResponseDto,
  UpdateProfileDto,
  UpdatePreferencesDto,
} from '../../api/generated/client';
import type { User, AvailableContext } from './auth.types';
import type { ServerPreferencePayload } from '@finance/contracts';
import type { ApiSuccessEnvelope } from '../../api/select-api-data';
import { selectApiData } from '../../api/select-api-data';

export type PreferencesReadResponse = Record<string, unknown>;

export async function authControllerLogin(params: LoginDto): Promise<LoginResponseDto> {
  const response = await authControllerLoginGenerated(params);
  return selectApiData(response as unknown as ApiSuccessEnvelope<LoginResponseDto>);
}

export async function authControllerVerifyChallengeTotp(
  challengeToken: string,
  body: ChallengeTotpDto,
): Promise<LoginResponseDto> {
  const response = await authControllerVerifyChallengeTotpGenerated(challengeToken, body);
  return selectApiData(response as unknown as ApiSuccessEnvelope<LoginResponseDto>);
}

export async function authControllerVerifyChallengeRecoveryCode(
  challengeToken: string,
  body: ChallengeRecoveryCodeDto,
): Promise<LoginResponseDto> {
  const response = await authControllerVerifyChallengeRecoveryCodeGenerated(challengeToken, body);
  return selectApiData(response as unknown as ApiSuccessEnvelope<LoginResponseDto>);
}

export async function authControllerSelectContext(
  payload: SelectContextDto,
): Promise<LoginResponseDto> {
  const response = await authControllerSelectContextGenerated(payload);
  return selectApiData(response as unknown as ApiSuccessEnvelope<LoginResponseDto>);
}

export async function authControllerForgotPassword(payload: ForgotPasswordDto): Promise<void> {
  await authControllerForgotPasswordGenerated(payload);
}

export async function authControllerResetPassword(payload: ResetPasswordDto): Promise<void> {
  await authControllerResetPasswordGenerated(payload);
}

export async function authControllerLogout(): Promise<void> {
  await authControllerLogoutGenerated({});
}

export async function authControllerRefreshSession(): Promise<{ accessToken?: string }> {
  const response = await authControllerRefreshGenerated({});
  const payload = selectApiData(response as unknown as ApiSuccessEnvelope<AccessTokenResponseDto>);
  return { accessToken: payload.accessToken };
}

export async function authControllerSwitchContext(payload: {
  membershipId: string;
  branchId?: string;
  clientAccountId?: string;
  portfolioAccountId?: string;
}): Promise<{ accessToken: string }> {
  const response = await authControllerSwitchContextGenerated(payload);
  const payloadData = selectApiData(response as unknown as ApiSuccessEnvelope<AccessTokenResponseDto>);
  return { accessToken: payloadData.accessToken! };
}

export async function authControllerEnterDelegation(delegationId: string): Promise<{ accessToken: string }> {
  const response = await authControllerEnterDelegationGenerated(delegationId);
  const payload = selectApiData(response as unknown as ApiSuccessEnvelope<AccessTokenResponseDto>);
  return { accessToken: payload.accessToken! };
}

export async function authControllerExitDelegation(): Promise<{ accessToken: string }> {
  const response = await authControllerExitDelegationGenerated();
  const payload = selectApiData(response as unknown as ApiSuccessEnvelope<AccessTokenResponseDto>);
  return { accessToken: payload.accessToken! };
}

export async function authControllerChangeInitialPassword(
  challengeToken: string,
  newPassword: string,
): Promise<LoginResponseDto> {
  const response = await authControllerChangeInitialPasswordGenerated({ challengeToken, newPassword });
  return selectApiData(response as unknown as ApiSuccessEnvelope<LoginResponseDto>);
}

export async function authControllerMe(): Promise<ProfileResponse> {
  const response = await authControllerMeGenerated();
  return selectApiData(response as unknown as ApiSuccessEnvelope<ProfileResponse>);
}

export async function authControllerUpdateProfile(params: UpdateProfileDto): Promise<ProfileResponse> {
  const response = await settingsProfileUpdateGenerated(params);
  return selectApiData(response as unknown as ApiSuccessEnvelope<ProfileResponse>);
}

export async function authControllerReadPreferences(): Promise<PreferencesReadResponse> {
  const response = await settingsPreferencesReadGenerated();
  return selectApiData(response as unknown as ApiSuccessEnvelope<PreferencesReadResponse>);
}

export async function authControllerUpdatePreferences(params: ServerPreferencePayload): Promise<Record<string, unknown>> {
  const response = await settingsPreferencesUpdateGenerated(params as unknown as UpdatePreferencesDto);
  return selectApiData(response as unknown as ApiSuccessEnvelope<Record<string, unknown>>);
}

export function mapAvailableContext(ctx: AvailableContextResponseDto): AvailableContext {
  return {
    membershipId: ctx.membershipId,
    organizationId: ctx.organizationId,
    organizationName: ctx.organizationName,
    branchId: ctx.branchId ?? undefined,
    branchName: ctx.branchName ?? undefined,
  };
}

export function mapLoginUser(
  user: AuthenticatedUserResponseDto,
  mfaEnrolled?: boolean,
): User {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    phone: null,
    status: 'ACTIVE',
    mfa: {
      enabled: mfaEnrolled ?? false,
      methods: [],
      recoveryCodesRemaining: 0,
    },
    memberships: [],
  };
}
